import React, { useState, useEffect, useCallback } from 'react';
import { View, Text, ScrollView } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useThemeColors } from '@/lib/useThemeColors';
import { capitalizeLabel, labelThemeColor } from '@/lib/scoring/score-display';
import { handleError } from '@/lib/errors';

// ─── Types ────────────────────────────────────────────────────────────────────

type TabId = 'summary' | 'narrative' | 'dd-prep';

// Phase 116.6 — accepts either legacy `{ dimension, name }` or v2 atom `{ id }` shape.
// Strength/weakness are legacy fields that don't exist on v2 atoms; consumers
// fall back to dimension-level feedback when these are absent.
interface DimensionItem {
  dimension?: string;
  id?: string;
  name?: string;
  rawScore: number;
  strength?: string;
  weakness?: string;
  feedback?: string;
}

interface RiskItem {
  description: string;
  severity: string;
  mitigation?: string;
}

interface ExecutiveSummaryProps {
  companyName: string;
  stage: string;
  sector: string;
  location?: string;
  foundedYear?: number;
  teamSize?: number;
  monthlyRevenue?: number;
  ventureLabel: string;
  ventureScore: number;
  evidenceLabel: string;
  dealReadinessPercent?: number;
  dealReadinessPresent?: number;
  dealReadinessTotal?: number;
  dimensions: DimensionItem[];
  risks: RiskItem[];
  recommendations: string[];
}

// ─── Storage key ─────────────────────────────────────────────────────────────

const TAB_STORAGE_KEY = 'inverio_exec_summary_tab';

// ─── Helper: sort dimensions ──────────────────────────────────────────────────

function topDims(dims: DimensionItem[], n: number): DimensionItem[] {
  return [...dims].sort((a, b) => b.rawScore - a.rawScore).slice(0, n);
}

function bottomDims(dims: DimensionItem[], n: number): DimensionItem[] {
  return [...dims].sort((a, b) => a.rawScore - b.rawScore).slice(0, n);
}

// ─── Tab Bar ──────────────────────────────────────────────────────────────────

interface TabBarProps {
  active: TabId;
  onChange: (tab: TabId) => void;
  colors: any;
}

const TABS: Array<{ id: TabId; label: string }> = [
  { id: 'summary', label: 'Summary' },
  { id: 'narrative', label: 'Narrative' },
  { id: 'dd-prep', label: 'DD Prep' },
];

function TabBar({ active, onChange, colors }: TabBarProps) {
  return (
    <View
      style={{
        flexDirection: 'row',
        backgroundColor: colors.surface.secondary,
        borderRadius: 8,
        padding: 3,
        marginBottom: 12,
      }}
    >
      {TABS.map((tab) => {
        const isActive = tab.id === active;
        return (
          <View
            key={tab.id}
            style={{
              flex: 1,
              height: 30,
              borderRadius: 6,
              backgroundColor: isActive ? colors.surface.elevated : 'transparent',
              overflow: 'hidden',
            }}
          >
            <View
              style={{
                flex: 1,
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Text
                style={{
                  color: isActive ? colors.text.primary : colors.text.tertiary,
                  fontSize: 12,
                  fontWeight: isActive ? '600' : '400',
                }}
                numberOfLines={1}
                onPress={() => onChange(tab.id)}
              >
                {tab.label}
              </Text>
            </View>
          </View>
        );
      })}
    </View>
  );
}

// ─── Bullet row ───────────────────────────────────────────────────────────────

function BulletRow({
  text,
  score,
  color,
  colors,
}: {
  text: string;
  score?: number;
  color?: string;
  colors: any;
}) {
  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'flex-start',
        marginBottom: 6,
      }}
    >
      <View
        style={{
          width: 4,
          height: 4,
          borderRadius: 2,
          backgroundColor: color ?? colors.text.tertiary,
          marginTop: 6,
          marginRight: 8,
          flexShrink: 0,
        }}
      />
      <Text
        style={{
          color: colors.text.secondary,
          fontSize: 13,
          lineHeight: 20,
          flex: 1,
        }}
      >
        {text}
        {score !== undefined ? (
          <Text style={{ color: colors.text.tertiary, fontSize: 12 }}> ({score})</Text>
        ) : null}
      </Text>
    </View>
  );
}

// ─── Checklist Item ───────────────────────────────────────────────────────────

function ChecklistItem({ text, colors }: { text: string; colors: any }) {
  return (
    <View style={{ flexDirection: 'row', alignItems: 'flex-start', marginBottom: 8 }}>
      <View
        style={{
          width: 16,
          height: 16,
          borderRadius: 3,
          borderWidth: 1.5,
          borderColor: colors.border.medium,
          marginRight: 10,
          marginTop: 2,
          flexShrink: 0,
        }}
      />
      <Text
        style={{
          color: colors.text.secondary,
          fontSize: 13,
          lineHeight: 20,
          flex: 1,
        }}
      >
        {text}
      </Text>
    </View>
  );
}

// ─── Section Header ───────────────────────────────────────────────────────────

function SectionHeader({ title, colors }: { title: string; colors: any }) {
  return (
    <Text
      style={{
        color: colors.text.tertiary,
        fontSize: 9,
        fontWeight: '700',
        textTransform: 'uppercase',
        letterSpacing: 1.2,
        marginBottom: 10,
        marginTop: 14,
      }}
    >
      {title}
    </Text>
  );
}

// ─── Company Context Line ─────────────────────────────────────────────────────

function composeContextLine(props: ExecutiveSummaryProps): string {
  const parts: string[] = [props.stage, props.sector];
  if (props.location) parts.push(props.location);
  if (props.foundedYear) parts.push(`est. ${props.foundedYear}`);
  if (props.teamSize) parts.push(`${props.teamSize}-person team`);
  if (props.monthlyRevenue) {
    const rev =
      props.monthlyRevenue >= 1000
        ? `$${Math.round(props.monthlyRevenue / 1000)}K MRR`
        : `$${props.monthlyRevenue} MRR`;
    parts.push(rev);
  }
  return parts.join(' · ');
}

// Display-name picker — handles v2 atoms (id only) and legacy shape (dimension + name).
function dimDisplayName(d: DimensionItem): string {
  return d.name ?? d.id ?? d.dimension ?? '';
}

// ─── Summary Tab ──────────────────────────────────────────────────────────────

function SummaryTab(props: ExecutiveSummaryProps & { colors: any }) {
  const { colors } = props;
  const top3 = topDims(props.dimensions, 3);
  const bottom3 = bottomDims(props.dimensions, 3);
  const vcColor = labelThemeColor(props.ventureLabel, colors);
  const verdict = `${props.companyName} is rated ${capitalizeLabel(props.ventureLabel)} (${props.ventureScore}) with ${props.evidenceLabel.toLowerCase()} evidence quality.`;

  return (
    <View>
      {/* Company header */}
      <Text
        style={{ color: colors.text.primary, fontSize: 15, fontWeight: '700', marginBottom: 2 }}
        numberOfLines={1}
      >
        {props.companyName}
      </Text>
      <Text
        style={{ color: colors.text.tertiary, fontSize: 12, marginBottom: 14 }}
        numberOfLines={2}
      >
        {composeContextLine(props)}
      </Text>

      <SectionHeader title="Strengths" colors={colors} />
      {top3.map((d, i) => (
        <BulletRow
          key={`str-${i}`}
          text={d.strength ?? d.feedback ?? dimDisplayName(d)}
          score={d.rawScore}
          color={colors.success}
          colors={colors}
        />
      ))}

      <SectionHeader title="Concerns" colors={colors} />
      {bottom3.map((d, i) => (
        <BulletRow
          key={`con-${i}`}
          text={d.weakness ?? d.feedback ?? dimDisplayName(d)}
          score={d.rawScore}
          color={colors.error}
          colors={colors}
        />
      ))}

      <SectionHeader title="Verdict" colors={colors} />
      <Text
        style={{
          color: colors.text.secondary,
          fontSize: 13,
          lineHeight: 20,
          fontStyle: 'italic',
        }}
      >
        <Text style={{ color: vcColor, fontWeight: '600', fontStyle: 'normal' }}>
          {capitalizeLabel(props.ventureLabel)}
        </Text>{' '}
        — {verdict}
      </Text>
    </View>
  );
}

// ─── Narrative Tab ────────────────────────────────────────────────────────────

function NarrativeTab(props: ExecutiveSummaryProps & { colors: any }) {
  const { colors } = props;
  const top3 = topDims(props.dimensions, 3);
  const bottom3 = bottomDims(props.dimensions, 3);
  const vcColor = labelThemeColor(props.ventureLabel, colors);

  // P1 — Strengths narrative
  const strengthPoints = top3
    .map((d) => d.strength ?? `strong ${dimDisplayName(d)} (${d.rawScore})`)
    .join(', ');
  const p1 = `${props.companyName} demonstrates notable strengths across ${top3.map(dimDisplayName).join(', ')}. ${strengthPoints ? `The team shows ${strengthPoints}.` : ''}`.trim();

  // P2 — Concerns + evidence + deal readiness
  const weakPoints = bottom3.map((d) => d.weakness ?? d.feedback ?? `${dimDisplayName(d)} needs attention`).join('. ');
  const drText =
    props.dealReadinessPercent !== undefined
      ? ` Deal readiness stands at ${props.dealReadinessPercent}% (${props.dealReadinessPresent ?? '?'}/${props.dealReadinessTotal ?? '?'} documents).`
      : '';
  const p2 = `Areas of concern include ${bottom3.map(dimDisplayName).join(', ')}. ${weakPoints}.${drText} Evidence quality is ${props.evidenceLabel.toLowerCase()}.`;

  // P3 — Verdict + next steps
  const firstRec = props.recommendations[0] ?? 'Review key dimensions before proceeding.';
  const p3 = `Overall, ${props.companyName} is rated ${capitalizeLabel(props.ventureLabel)} with a venture score of ${props.ventureScore}. The recommended next step is: ${firstRec}`;

  return (
    <View>
      {/* Company header */}
      <Text
        style={{ color: colors.text.primary, fontSize: 15, fontWeight: '700', marginBottom: 2 }}
        numberOfLines={1}
      >
        {props.companyName}
      </Text>
      <Text
        style={{ color: colors.text.tertiary, fontSize: 12, marginBottom: 16 }}
        numberOfLines={2}
      >
        {composeContextLine(props)}
      </Text>

      {/* P1 */}
      <Text
        style={{
          color: colors.text.secondary,
          fontSize: 13,
          lineHeight: 20,
          marginBottom: 12,
        }}
      >
        {p1}
      </Text>

      {/* P2 */}
      <Text
        style={{
          color: colors.text.secondary,
          fontSize: 13,
          lineHeight: 20,
          marginBottom: 12,
        }}
      >
        {p2}
      </Text>

      {/* P3 */}
      <Text
        style={{
          color: colors.text.secondary,
          fontSize: 13,
          lineHeight: 20,
        }}
      >
        {p3.replace('rated ', 'rated ')}
        {' '}
        <Text style={{ color: vcColor, fontWeight: '700' }}>
          {capitalizeLabel(props.ventureLabel)}
        </Text>
        {p3.includes(capitalizeLabel(props.ventureLabel))
          ? p3.slice(p3.indexOf(capitalizeLabel(props.ventureLabel)) + capitalizeLabel(props.ventureLabel).length)
          : ''}
      </Text>
    </View>
  );
}

// ─── DD Prep Tab ──────────────────────────────────────────────────────────────

function DDPrepTab(props: ExecutiveSummaryProps & { colors: any }) {
  const { colors } = props;
  const highRisks = props.risks.filter((r) => r.severity === 'high');
  const mediumRisks = props.risks.filter((r) => r.severity === 'medium');

  const p = `Use this checklist to prepare for due diligence on ${props.companyName}. Address high-priority items before your first investor meeting.`;

  return (
    <View>
      <Text
        style={{ color: colors.text.primary, fontSize: 15, fontWeight: '700', marginBottom: 6 }}
        numberOfLines={1}
      >
        {props.companyName}
      </Text>
      <Text
        style={{
          color: colors.text.secondary,
          fontSize: 13,
          lineHeight: 19,
          marginBottom: 16,
        }}
      >
        {p}
      </Text>

      <SectionHeader title="Before First Meeting" colors={colors} />
      {highRisks.length === 0 ? (
        <Text style={{ color: colors.text.tertiary, fontSize: 13, marginBottom: 8 }}>
          No high-severity risks identified.
        </Text>
      ) : (
        highRisks.map((r, i) => (
          <ChecklistItem key={`high-${i}`} text={r.description} colors={colors} />
        ))
      )}

      <SectionHeader title="Before Term Sheet" colors={colors} />
      {mediumRisks.length === 0 && props.recommendations.length === 0 ? (
        <Text style={{ color: colors.text.tertiary, fontSize: 13 }}>
          No medium-severity items identified.
        </Text>
      ) : (
        <>
          {mediumRisks.map((r, i) => (
            <ChecklistItem key={`med-${i}`} text={r.description} colors={colors} />
          ))}
          {props.recommendations.slice(0, 3).map((rec, i) => (
            <ChecklistItem key={`rec-${i}`} text={rec} colors={colors} />
          ))}
        </>
      )}
    </View>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function ExecutiveSummarySection(props: ExecutiveSummaryProps) {
  const colors = useThemeColors();
  const [activeTab, setActiveTab] = useState<TabId>('narrative');

  // Persist tab preference
  useEffect(() => {
    AsyncStorage.getItem(TAB_STORAGE_KEY)
      .then((val) => {
        if (val === 'summary' || val === 'narrative' || val === 'dd-prep') {
          setActiveTab(val);
        }
      })
      .catch((err) => {
        handleError(err, { context: 'ExecutiveSummarySection.loadTab', silent: true });
      });
  }, []);

  const handleTabChange = useCallback((tab: TabId) => {
    setActiveTab(tab);
    AsyncStorage.setItem(TAB_STORAGE_KEY, tab).catch((err) => {
      handleError(err, { context: 'ExecutiveSummarySection.saveTab', silent: true });
    });
  }, []);

  return (
    <View
      style={{
        backgroundColor: colors.surface.secondary,
        borderWidth: 1,
        borderColor: colors.border.light,
        borderRadius: 14,
        padding: 14,
        marginBottom: 12,
      }}
    >
      {/* Section label */}
      <Text
        style={{
          color: colors.text.tertiary,
          fontSize: 10,
          fontWeight: '700',
          textTransform: 'uppercase',
          letterSpacing: 1.5,
          marginBottom: 12,
        }}
      >
        Executive Summary
      </Text>

      <TabBar active={activeTab} onChange={handleTabChange} colors={colors} />

      {/* Content area */}
      <View
        style={{
          backgroundColor: colors.surface.primary,
          borderRadius: 10,
          padding: 12,
        }}
      >
        {activeTab === 'summary' && <SummaryTab {...props} colors={colors} />}
        {activeTab === 'narrative' && <NarrativeTab {...props} colors={colors} />}
        {activeTab === 'dd-prep' && <DDPrepTab {...props} colors={colors} />}
      </View>
    </View>
  );
}
